import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import AiStarOrangeIcon from "@redotech/redo-icons/ai-star-orange.svg";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ImsPermissions } from "@redotech/redo-model/permissions";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useIntelligentInventoryFlag } from "../../../../intelligent-inventory-flag-provider";
import * as styles from "./intelligent-inventory.module.css";

const CHAT_HISTORY_LIMIT = 4;

export const IntelligentInventoryItem = memo(
  function IntelligentInventoryItem() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const location = useLocation();
    const isIntelligentInventoryUser = useIntelligentInventoryFlag();
    const canAccessIntelligentInventory = useHasPermission(
      ImsPermissions.ACCESS_INTELLIGENT_INVENTORY,
    );
    const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);

    const isHidden =
      !isIntelligentInventoryUser || !canAccessIntelligentInventory;

    const isIntelligentInventoryChildSelected = location.pathname.includes(
      "/intelligent-inventory/",
    );

    // Extract the current threadId from the URL so we re-fetch when it changes
    // (e.g., when a new chat is created and the URL updates)
    const currentThreadId = location.pathname.match(
      /\/intelligent-inventory\/([^/]+)/,
    )?.[1];

    const recentThreadsLoad = useLoad(
      async (signal) => {
        if (!inventoryRpcClient || !isIntelligentInventoryUser) {
          return [];
        }
        try {
          const result =
            await inventoryRpcClient.listIntelligentInventoryThreads(
              { limit: CHAT_HISTORY_LIMIT },
              { signal },
            );
          return result.threads;
        } catch (error) {
          console.error("Failed to fetch catalog agent threads", error);
          return [];
        }
      },
      [inventoryRpcClient, isIntelligentInventoryUser, currentThreadId],
    );

    const threads = recentThreadsLoad.value ?? [];
    const hasThreads = threads.length > 0;

    if (!hasThreads) {
      return (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/intelligent-inventory`,
          }}
          beta
          FilledIcon={AiStarOrangeIcon}
          friendlyName="Intelligent Inventory"
          hidden={isHidden}
          id="intelligent-inventory"
          UnfilledIcon={AiStarIcon}
        />
      );
    }

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/intelligent-inventory`,
        }}
        anyChildSelected={isIntelligentInventoryChildSelected}
        beta
        FilledIcon={AiStarOrangeIcon}
        friendlyName="Intelligent Inventory"
        hidden={isHidden}
        id="intelligent-inventory"
        UnfilledIcon={AiStarIcon}
      >
        {threads.map((thread) => (
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/intelligent-inventory/${thread.threadId}`,
            }}
            friendlyName={thread.title}
            id={`intelligent-inventory-thread-${thread.threadId}`}
            key={thread.threadId}
          />
        ))}
        <div className={styles.seeAll}>
          <Text fontSize="xs" textColor="tertiary">
            See all
          </Text>
        </div>
      </RedoSecondaryNavItem>
    );
  },
);
